import { salesDocService } from '../../helpers/services/salesDoc.service'
import i18n from '@/i18n'
import { sharedHelper, filtersHelper } from '@/utils'

export const state = {
  list: { data: [], pagination: { pageNumber: 0, pageSize: 0, lastPage: 0, tableRowsCount: 0 } },
  details: {},
  consignmentBalance: [],
  status: { list: true, detail: true, save: true },
}

export const SALES_DOC_MODULE = 'salesDoc'

export const GET_ALL_SALES_DOCS = 'getAllSalesDocs'
export const GET_SALES_DOC = 'getSalesDoc'
export const CREATE_SALES_DOC = 'createSalesDoc'
export const UPDATE_SALES_DOC = 'updateSalesDoc'
export const DELETE_SALES_DOC = 'deleteSalesDoc'
export const GET_CONSIGNMENT_INVENTORY = 'getConsignmentInventory'
export const GET_CONSIGNMENT_BALANCE = 'getConsignmentBalance'
export const CONVERT_SO_TO_SI = 'convertSoToSi'
export const MARK_QUOTATION_WON = 'markQuotationWon'
export const MARK_QUOTATION_LOST = 'markQuotationLost'
export const GET_CUSTOMER_PRICE = 'getCustomerPrice'
export const GET_QUOTATION_STATUS = 'getQuotationStatus'

const REQUESTING = 'requesting'
const SUCCESS = 'commonSuccess'
const FAILURE = 'commonFailure'
const LIST_SUCCESS = 'listSuccess'
const DETAIL_SUCCESS = 'detailSuccess'
const BALANCE_SUCCESS = 'balanceSuccess'

/**
 * A 202 means nothing was written to the accounting system: the document is
 * parked as a pending approval, either because approval mode is manual or
 * because a credit gate caught it. Telling the user it was "created" would be
 * a lie — it does not exist anywhere yet.
 */
function heldForApproval(r) {
  return !!(r?.pending_approval || r?.is_exceed_limit)
}

function mapRow(item) {
  return {
    docNo: item.doc_no,
    debtorCode: item.debtor_code,
    debtorName: item.debtor_name,
    docDate: filtersHelper.getDateFormat(item.doc_date),
    description: item.description,
    currencyCode: item.currency_code,
    total: item.total,
    agent: item.agent,
    priceBinding: item.price_binding,
    priceBindingUntil: item.price_binding_until || null,
    quotationStatus: item.quotation_status,
    isConverted: item.is_converted,
    convertedToSi: item.converted_to_si,
    // A document the ledger does not have yet — it is a held approval, and
    // opens from the approval rather than from the ledger.
    approvalUuid: item.approval_uuid || null,
    approvalStatus: item.approval_status || null,
    approvalAction: item.approval_action || null,
    sourceDocNo: item.source_doc_no || null,
    // A ledger document with an edit waiting on it.
    pendingEditUuid: item.pending_edit_uuid || null,
  }
}

export const actions = {
  [GET_ALL_SALES_DOCS]({ commit }, { docType, filter }) {
    commit(REQUESTING, 'list')
    salesDocService.getAllSalesDocs(docType, filter).then((r) => {
      commit(LIST_SUCCESS, r); commit(SUCCESS)
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_SALES_DOC]({ commit }, { docType, docNo }) {
    commit(REQUESTING, 'detail')
    return salesDocService.getSalesDoc(docType, docNo).then((r) => {
      commit(DETAIL_SUCCESS, r); commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [CREATE_SALES_DOC]({ commit, dispatch }, { docType, data, filter }) {
    commit(REQUESTING, 'save')
    return salesDocService.createSalesDoc(docType, data).then((r) => {
      sharedHelper.alertSuccessMessage(
        heldForApproval(r) ? i18n.t('label.submittedForApproval') : i18n.t('label.createX', [docType])
      )
      commit(SUCCESS); if (filter) dispatch(GET_ALL_SALES_DOCS, { docType, filter }); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
      throw e
    })
  },
  [UPDATE_SALES_DOC]({ commit }, { docType, docNo, data }) {
    commit(REQUESTING, 'save')
    return salesDocService.updateSalesDoc(docType, docNo, data).then((r) => {
      sharedHelper.alertSuccessMessage(
        heldForApproval(r) ? i18n.t('label.submittedForApproval') : i18n.t('label.updateX', [docType])
      )
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
      throw e
    })
  },
  [DELETE_SALES_DOC]({ commit, dispatch }, { docType, docNo, filter }) {
    commit(REQUESTING, 'save')
    return salesDocService.deleteSalesDoc(docType, docNo).then((r) => {
      commit(SUCCESS); if (filter) dispatch(GET_ALL_SALES_DOCS, { docType, filter }); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_CONSIGNMENT_INVENTORY]({ commit }, { docNo }) {
    commit(REQUESTING, 'detail')
    return salesDocService.getConsignmentInventory(docNo).then((r) => {
      commit(DETAIL_SUCCESS, r); commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [GET_CONSIGNMENT_BALANCE]({ commit }, { debtorCode }) {
    return salesDocService.getConsignmentBalance(debtorCode).then((r) => {
      commit(BALANCE_SUCCESS, r); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code)
    })
  },
  [CONVERT_SO_TO_SI]({ commit }, { docNo }) {
    commit(REQUESTING, 'save')
    return salesDocService.convertSoToSi(docNo).then((r) => {
      sharedHelper.alertSuccessMessage(
        heldForApproval(r) ? i18n.t('label.submittedForApproval') : 'Sales Order converted to Sales Invoice'
      )
      commit(SUCCESS); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code); commit(FAILURE)
    })
  },
  [MARK_QUOTATION_WON](_ctx, { docNo, data }) {
    return salesDocService.markQuotationWon(docNo, data).then((r) => {
      sharedHelper.alertSuccessMessage('Quotation marked as Won'); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code)
    })
  },
  [MARK_QUOTATION_LOST](_ctx, { docNo, data }) {
    return salesDocService.markQuotationLost(docNo, data).then((r) => {
      sharedHelper.alertSuccessMessage('Quotation marked as Lost'); return r
    }).catch((e) => {
      sharedHelper.alertErrorMessage(sharedHelper.getErrorMessage(e), e?.response?.data?.code)
    })
  },
  [GET_CUSTOMER_PRICE](_ctx, { debtor_code, item_code, uom, qty }) {
    return salesDocService.getCustomerPrice({ debtor_code, item_code, uom, qty }).then((r) => r).catch(() => null)
  },
  [GET_QUOTATION_STATUS](_ctx, { docNo }) {
    return salesDocService.getQuotationStatus(docNo).then((r) => r).catch(() => null)
  },
}

export const mutations = {
  [LIST_SUCCESS](state, data) {
    const items = data.data || []
    state.list.pagination = sharedHelper.apiPaginationAdapter(data.pagination)
    state.list.data = data.success ? items.map(mapRow) : items
  },
  [DETAIL_SUCCESS](state, data) {
    state.details = data.data || {}
  },
  [BALANCE_SUCCESS](state, data) {
    state.consignmentBalance = data.data || []
  },
  [FAILURE](state) { state.status = { list: true, detail: true, save: true } },
  [REQUESTING](state, type) { state.status[type] = false },
  [SUCCESS](state) { state.status = { list: true, detail: true, save: true } },
}
